// Notification rules shared by the server writers and the client bell — no
// prisma, no "server-only", so both sides use one source of truth and cannot
// drift (same split as reviews-shared.ts / reviews.ts).

/** Booking events a guest is told about. Also the message keys under `Notifications`. */
export const BOOKING_KINDS = [
  "approved",
  "paid",
  "cancelled",
  "rejected",
  "cancelRequested",
  "expired",
] as const;

export type BookingKind = (typeof BOOKING_KINDS)[number];
export type NotificationKind = BookingKind | "offer";

/** The coloured "light" shown next to an item. */
export type Light = "green" | "red" | "amber" | "blue";

/**
 * Green = it went your way, red = it did not, amber = you need to act,
 * blue = marketing. Derived from the kind and never stored, so the colour can
 * never disagree with the event.
 */
export function lightFor(kind: string): Light {
  switch (kind) {
    case "approved":
    case "paid":
      return "green";
    case "cancelRequested":
      return "amber";
    case "offer":
      return "blue";
    default:
      return "red";
  }
}

/**
 * How each light is painted. A translucent wash plus a coloured icon rather
 * than a saturated disc: in a feed the sentence should lead and the colour is
 * metadata, so a wall of solid colour blocks competes with what it labels.
 *
 * The tint is opacity-based on purpose. Dark mode here works by overriding
 * tokens, and literal light shades (emerald-50 and friends) do not flip — a
 * translucent tint sits correctly on both grounds.
 *
 * Note `forest-*` is PURPLE in this design system, so "green" is a literal
 * emerald — the same choice the admin calendar makes for booked days.
 */
export const LIGHT_STYLES: Record<Light, { tint: string; icon: string }> = {
  green: { tint: "bg-emerald-500/15", icon: "text-emerald-600" },
  red: { tint: "bg-red-500/15", icon: "text-red-600" },
  amber: { tint: "bg-amber-500/20", icon: "text-amber-600" },
  blue: { tint: "bg-clay/15", icon: "text-clay" },
};

/**
 * Which icon a row shows. Distinct per EVENT, not per colour: "approved" and
 * "payment received" are both green, but a calendar tick and a card say
 * different things, and that meaning would otherwise rest on colour alone.
 */
export type IconName =
  | "calendarCheck"
  | "card"
  | "calendarX"
  | "clock"
  | "alert"
  | "tag";

export function iconFor(kind: string): IconName {
  switch (kind) {
    case "approved":
      return "calendarCheck";
    case "paid":
      return "card";
    case "cancelRequested":
      return "alert";
    case "expired":
      return "clock"; // a lapsed deadline, not a decision someone made
    case "offer":
      return "tag";
    default:
      return "calendarX"; // cancelled / rejected
  }
}

/** Unread badge text: nothing at zero, capped at "9+". */
export function badgeLabel(unread: number): string {
  if (!Number.isFinite(unread) || unread <= 0) return "";
  return unread > 9 ? "9+" : String(Math.floor(unread));
}

/**
 * Only same-site, locale-relative paths are allowed as link targets — the offer
 * link is admin-typed free text. Blocks "javascript:", absolute URLs and
 * protocol-relative "//evil.com".
 */
export function safeHref(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim();
  if (!v.startsWith("/") || v.startsWith("//")) return null;
  // The WHATWG URL parser treats "\" as "/", so "/\evil.com" would become
  // protocol-relative. Nothing on this site needs a backslash in a path.
  if (v.includes("\\")) return null;
  return v;
}

/** Localized value copy — kept here so the client bell doesn't pull in the
 *  whole seed-content module just to read one field. */
export type LocalizedText = { hy: string; ru: string; en: string };

export function pickLocalized(
  value: unknown,
  locale: string,
): string {
  if (!value || typeof value !== "object") return "";
  const v = value as Record<string, unknown>;
  const pick = (k: string) => {
    const raw = v[k];
    return typeof raw === "string" && raw.trim() ? raw : "";
  };
  // `??` alone would keep an EMPTY string for the viewer's language instead of
  // falling back, which renders a blank card.
  return pick(locale) || pick("en");
}

/** One row as the API hands it to the bell. Text is resolved on the client so
 *  it follows the language the guest is viewing right now. */
export type NotificationItem = {
  id: string;
  category: "BOOKING" | "OFFER";
  kind: string;
  light: Light;
  /** ICU variables for BOOKING rows (stay name as {hy,ru,en}, dates, nights). */
  data: Record<string, unknown> | null;
  /** OFFER rows only — admin-authored, already {hy,ru,en}. */
  title: unknown | null;
  body: unknown | null;
  href: string | null;
  createdAt: string;
  read: boolean;
};
